import { ResourcelessPermissionId } from '@upright/common/iam/privilege/permission/types';
import { SubscriptionFeature } from '@upright/common/subscription/subscription-features';
import { useHasResourcelessPrivilege } from '@upright/ui/api/iam/privilege/query-options';
import { createContext, type ReactNode, useContext } from 'react';
import { useGetHasAccessToFeatureWithCurrentSubscription } from '../hooks/use-get-access-to-feature-with-subscription';

/**
 * Who sees product attributes in the product editor: the "Show attributes" toggle and the
 * Attributes / Advanced attributes tabs of the Edit product dialog. Display only: attributes count
 * in the results whoever sees them, and the server does not check this.
 *
 * Attributes are part of the DMA plan, so Enterprise organisations and team users see them. An
 * investor's custom company is the exception (PLT-7129): a user with the chat company creation
 * rollout switch sees them there too, so a trial organisation can check the attributes the chat
 * read from its files before approving the set. The editor cannot tell what kind of company it
 * holds, so the surfaces that edit an investor's custom company wrap it in
 * `CustomCompanyAttributeAccess`. Every other surface (the DMA dashboard and onboarding, the
 * consultant wizard, suppliers) keeps the plan rule.
 */
const CustomCompanyAttributeAccessContext = createContext(false);

export function CustomCompanyAttributeAccess({
  enabled = true,
  children,
}: {
  /** False where the same surface edits a supplier, which keeps the plan rule. */
  enabled?: boolean;
  children: ReactNode;
}) {
  const { hasPrivilege } = useHasResourcelessPrivilege(ResourcelessPermissionId.USE_CHAT_COMPANY_CREATION);
  return (
    <CustomCompanyAttributeAccessContext.Provider value={enabled && hasPrivilege}>
      {children}
    </CustomCompanyAttributeAccessContext.Provider>
  );
}

export function useHasProductAttributeAccess(companyId: string | undefined): boolean {
  const { hasAccess } = useGetHasAccessToFeatureWithCurrentSubscription(
    SubscriptionFeature.PRODUCT_ATTRIBUTES,
    companyId,
  );
  const hasCustomCompanyAccess = useContext(CustomCompanyAttributeAccessContext);
  return hasAccess || hasCustomCompanyAccess;
}
